import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '@/lib/api-client';
import { SessionContext } from '@/lib/session-context';
import { useParams, useSearchParams } from 'react-router-dom';

/* ---------- Types ---------- */

interface LocationOption {
  location: string;
  completedSessions: number;
  threshold: number;
  available: boolean;
  noTasksLeft?: boolean;
}

type SessionPhase =
  | 'location-select'
  | 'mic-test'
  | 'active'
  | 'paused'
  | 'ended';

interface SessionTimerProps {
  /** Content to render inside the active session (e.g. RecordingInterface) */
  children?: React.ReactNode;
  /** Called when session ends (either explicitly or due to idle) */
  onSessionEnd?: () => void;
  mode?: 'recording' | 'collection';
}

const IDLE_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * SessionTimer — Manages the task session lifecycle.
 *
 * Responsibilities:
 * - Display location selector (disable locations at threshold)
 * - Integrate mic test before session start
 * - Track session start/end timestamps via backend
 * - Display elapsed active time (MM:SS)
 * - Detect 5-minute inactivity → auto-pause + notification
 * - Allow session resume after pause
 */
export function SessionTimer({ children, onSessionEnd, mode = 'recording' }: SessionTimerProps) {
  const [searchParams] = useSearchParams();
  const { projectId: routeProjectId } = useParams();
  const projectId = searchParams.get('project_id') || routeProjectId || null;
  const [phase, setPhase] = useState<SessionPhase>('location-select');
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [micTestPassed, setMicTestPassed] = useState(false);

  const timerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const sessionStartTimeRef = useRef<number | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionClosedRef = useRef(false);

  // ---------- Fetch locations on mount ----------
  useEffect(() => {
    fetchLocations();
  }, [projectId]);

  async function fetchLocations() {
    try {
      const response = await apiClient.get('/sessions/locations', {
        params: projectId ? { project_id: projectId, session_kind: mode } : { session_kind: mode },
      });
      const data: LocationOption[] = response.data.locations ?? response.data;
      setLocations(data);
    } catch {
      setError('Failed to load locations. Please refresh.');
    }
  }

  // ---------- Elapsed time ticker ----------
  useEffect(() => {
    if (phase === 'active') {
      timerIntervalRef.current = setInterval(() => {
        if (sessionStartTimeRef.current !== null) {
          const elapsed = Math.floor(
            (Date.now() - sessionStartTimeRef.current) / 1000
          );
          setElapsedSeconds(elapsed);
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [phase]);

  // ---------- Idle detection ----------
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    if (phase === 'active' && sessionId) {
      idleTimerRef.current = setTimeout(() => {
        handleIdlePause();
      }, IDLE_TIMEOUT_MS);
    }
  }, [phase, sessionId]);

  useEffect(() => {
    if (phase !== 'active') return;

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'touchstart'];

    const handleActivity = () => {
      resetIdleTimer();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Start the idle timer
    resetIdleTimer();

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [phase, resetIdleTimer]);

  // ---------- Handlers ----------

  async function handleIdlePause() {
    if (!sessionId) return;

    try {
      await apiClient.put(`/sessions/${sessionId}/end`);
    } catch {
      // Best-effort — the session is paused locally regardless
    }

    sessionClosedRef.current = true;
    setPhase('paused');
    onSessionEnd?.();
  }

  async function handleStartSession() {
    if (!selectedLocation) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.post('/sessions/start', {
        location: selectedLocation,
        project_id: projectId,
        session_kind: mode,
        client_context: {
          platform: navigator.platform || null,
          browser: /Edg\//.test(navigator.userAgent) ? 'Edge' : /Chrome\//.test(navigator.userAgent) ? 'Chrome' : /Safari\//.test(navigator.userAgent) ? 'Safari' : /Firefox\//.test(navigator.userAgent) ? 'Firefox' : 'Other',
          operating_system: /Windows/.test(navigator.userAgent) ? 'Windows' : /Android/.test(navigator.userAgent) ? 'Android' : /iPhone|iPad/.test(navigator.userAgent) ? 'iOS / iPadOS' : /Mac OS/.test(navigator.userAgent) ? 'macOS' : /Linux/.test(navigator.userAgent) ? 'Linux' : 'Other',
        },
      });
      const newSessionId = response.data.session?.id ?? response.data.sessionId ?? response.data.id;
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      sessionClosedRef.current = false;
      sessionStartTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setPhase('active');
    } catch {
      setError('Failed to start session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleEndSession() {
    if (!sessionId) return;

    setIsLoading(true);
    setError(null);

    try {
      await apiClient.put(`/sessions/${sessionId}/end`);
    } catch {
      // Best-effort
    } finally {
      setIsLoading(false);
      sessionClosedRef.current = true;
      setPhase('ended');
      onSessionEnd?.();
    }
  }

  async function handleResume() {
    // Reset state for a new session — user must re-select location
    setPhase('location-select');
    setSessionId(null);
    sessionIdRef.current = null;
    sessionClosedRef.current = false;
    setElapsedSeconds(0);
    sessionStartTimeRef.current = null;
    setMicTestPassed(false);
    setError(null);
    await fetchLocations();
  }

  function handleMicTestComplete() {
    setMicTestPassed(true);
  }

  function handleLocationSelect(locationId: string) {
    setSelectedLocation(locationId);
  }

  // Moving to another tab ends the active session. Once closed, its records are
  // intentionally immutable on the participant workspace.
  useEffect(() => () => {
    const activeSessionId = sessionIdRef.current;
    if (activeSessionId && !sessionClosedRef.current) {
      sessionClosedRef.current = true;
      apiClient.put(`/sessions/${activeSessionId}/end`).catch(() => {});
    }
  }, []);

  // ---------- Format time ----------
  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }

  // ---------- Render ----------

  return (
    <div className={phase === 'active' ? 'w-full p-5 lg:p-6' : 'mx-auto max-w-3xl p-5 lg:p-6'}>
      {/* Error banner */}
      {error && (
        <div
          role="alert"
          className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}

      {/* Phase: Location Selection */}
      {phase === 'location-select' && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <h2 className="text-xl font-semibold text-gray-900">
            Start {mode === 'recording' ? 'Recording' : 'Collection'} Session
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {mode === 'recording' ? 'Select your recording location and complete the microphone test to begin.' : 'Start a focused collection session. Uploads can be changed only until this session ends.'}
          </p>

          {/* Location selector — card-style buttons like consent page */}
          <div className="mt-6 space-y-3">
            <p className="text-sm font-medium text-gray-700">{mode === 'recording' ? 'Task Location' : 'Session'}</p>
            {locations.map((loc) => {
              const isSelected = selectedLocation === loc.location;
              const disabled = !loc.available;
              const label = loc.location === 'data_collection' ? 'Image collection' : loc.location.charAt(0).toUpperCase() + loc.location.slice(1);

              return (
                <button
                  key={loc.location}
                  type="button"
                  onClick={() => !disabled && handleLocationSelect(loc.location)}
                  disabled={disabled}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg border text-left transition ${
                    disabled
                      ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
                      : isSelected
                        ? 'border-primary-400 bg-primary-50 ring-2 ring-primary-300'
                        : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                  }`}
                >
                  <span className="text-2xl">
                    {loc.location === 'home' ? '🏠' : loc.location === 'office' ? '🏢' : loc.location === 'cafe' ? '☕' : loc.location === 'studio' ? '🎙️' : '📍'}
                  </span>
                  <span className="flex-1 font-medium text-gray-900">{label}</span>
                  {disabled ? (
                    <span className="text-xs text-gray-400 font-medium">
                      {loc.noTasksLeft ? 'No tasks left' : 'Limit reached'}
                    </span>
                  ) : isSelected ? (
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center text-xs font-bold">✓</span>
                  ) : (
                    <span className="text-sm text-primary-600 font-medium">Select</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Mic test section */}
          {mode === 'recording' && selectedLocation && !micTestPassed && (
            <div className="mt-6">
              <MicTestInline onComplete={handleMicTestComplete} />
            </div>
          )}

          {/* Start button — visible only when location selected + mic test passed */}
          {selectedLocation && (mode === 'collection' || micTestPassed) && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleStartSession}
                disabled={isLoading}
                className="rounded-md bg-green-600 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Starting…' : 'Begin Session'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Phase: Active Session */}
      {phase === 'active' && (
        <div>
          {/* Session bar */}
          <div className="mb-4 flex items-center justify-between rounded-lg border border-atlas-border bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
              </span>
              <span className="text-sm font-medium text-gray-700">
                Session Active
              </span>
            </div>

            <div className="flex items-center gap-4">
              <span
                className="font-mono text-lg font-semibold text-gray-900"
                aria-label="Elapsed active time"
              >
                {formatTime(elapsedSeconds)}
              </span>

              <button
                type="button"
                onClick={handleEndSession}
                disabled={isLoading}
                className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
              >
                End Session
              </button>
            </div>
          </div>

          {/* Session content (e.g., RecordingInterface) */}
          <SessionContext.Provider value={{ sessionId, location: selectedLocation }}>
            {children}
          </SessionContext.Provider>
        </div>
      )}

      {/* Phase: Paused (idle timeout) */}
      {phase === 'paused' && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex flex-col items-center text-center">
            <PauseIcon className="h-16 w-16 text-yellow-500" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Session Paused
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your session was paused due to inactivity (no activity for 5
              minutes). Your progress has been saved.
            </p>
            <p className="mt-1 text-sm text-gray-500">
              Total active time: {formatTime(elapsedSeconds)}
            </p>

            <button
              type="button"
              onClick={handleResume}
              className="mt-6 rounded-md bg-gray-900 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            >
              Start another session
            </button>
          </div>
        </div>
      )}

      {/* Phase: Ended */}
      {phase === 'ended' && (
        <div className="rounded-lg bg-white p-6 shadow-md">
          <div className="flex flex-col items-center text-center">
            <CheckCircleIcon className="h-16 w-16 text-green-500" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              Session Complete
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Your recording session has ended. Total active time:{' '}
              {formatTime(elapsedSeconds)}
            </p>

            <button
              type="button"
              onClick={handleResume}
              className="mt-6 rounded-md bg-gray-900 px-6 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
            >
              Start New Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Inline Mic Test (simplified for session flow) ---------- */

interface MicTestInlineProps {
  onComplete: () => void;
}

function MicTestInline({ onComplete }: MicTestInlineProps) {
  const [status, setStatus] = useState<'idle' | 'requesting' | 'testing' | 'passed' | 'denied'>('idle');
  const [volumeLevel, setVolumeLevel] = useState(0);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioDetectedRef = useRef(false);

  const cleanup = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  async function startTest() {
    setStatus('requesting');
    audioDetectedRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      setStatus('testing');

      const monitor = () => {
        analyser.getByteFrequencyData(dataArray);
        const sum = dataArray.reduce((acc, val) => acc + val, 0);
        const average = sum / dataArray.length;
        const normalized = Math.min(100, Math.round((average / 255) * 100 * 2.5));
        setVolumeLevel(normalized);

        if (normalized > 10 && !audioDetectedRef.current) {
          audioDetectedRef.current = true;
          setStatus('passed');
          // Submit mic test result and notify parent
          apiClient.post('/sessions/mic-test', { result: 'pass' }).catch(() => {});
          cleanup();
          onComplete();
        }

        if (!audioDetectedRef.current) {
          animationFrameRef.current = requestAnimationFrame(monitor);
        }
      };

      monitor();
    } catch {
      cleanup();
      setStatus('denied');
    }
  }

  if (status === 'idle') {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">
          Complete a microphone test before starting your session.
        </p>
        <button
          type="button"
          onClick={startTest}
          className="mt-3 rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2"
        >
          Test Microphone
        </button>
      </div>
    );
  }

  if (status === 'requesting') {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-center">
        <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
        <p className="mt-2 text-sm text-gray-600">Requesting microphone access…</p>
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">
          Microphone access denied. Please enable it in your browser settings and try again.
        </p>
        <button
          type="button"
          onClick={() => { setStatus('idle'); }}
          className="mt-2 text-sm font-medium text-red-800 underline hover:text-red-900"
        >
          Retry
        </button>
      </div>
    );
  }

  if (status === 'testing') {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm text-gray-600">Speak into your microphone…</p>
        <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className="h-full rounded-full bg-primary-500 transition-all duration-75"
            style={{ width: `${volumeLevel}%` }}
          />
        </div>
      </div>
    );
  }

  // passed — shouldn't render long since onComplete fires immediately
  return (
    <div className="rounded-md border border-green-200 bg-green-50 p-4">
      <p className="text-sm font-medium text-green-800">
        ✓ Microphone test passed
      </p>
    </div>
  );
}

/* ---------- Icons ---------- */

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.25 9v6m-4.5 0V9M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
